// The contributor's progress: validated audios, test status, and what can be
// claimed. Opens the "you can take the test" dialog once the threshold is met.
import { useEffect, useMemo, useRef, useState } from "react";
import { contributionsApi } from "@/api/contributions.api";
import paymentsApi from "@/api/payments.api";
import { testsApi, type ContributorTestEligibility } from "@/api/tests.api";
import { canAccessContributorWorkspace } from "@/lib/auth/roles";
import { computePayoutState, summarizeContributorStats, type BackendEligibility } from "../logic/earnings-logic";
import type { GetPaidT, GetPaidUser } from "./get-paid-context";

interface ContributorEarningsInput {
  user: GetPaidUser;
  tPage: GetPaidT["getPaidPage"];
  /** PENDING, APPROVED and PAID requests of the user. */
  pendingPayoutAmount: number;
  loadUserContracts: () => Promise<void>;
}

export function useContributorEarnings({ user, tPage, pendingPayoutAmount, loadUserContracts }: ContributorEarningsInput) {
  const [validatedAudios, setValidatedAudios] = useState<number>(0);
  const [pendingAudios, setPendingAudios] = useState<number>(0);
  const [totalContributions, setTotalContributions] = useState<number>(0);
  const [testApproved, setTestApproved] = useState<boolean>(false);
  const [, setLoadingUserStats] = useState<boolean>(false);
  const [validatedAudiosAfterTest, setValidatedAudiosAfterTest] = useState<number>(0);
  const [, setAcceptedAudiosByLanguage] = useState<Record<string, number>>({});
  const [testEligibility, setTestEligibility] = useState<ContributorTestEligibility | null>(null);
  const [backendEligibility, setBackendEligibility] = useState<BackendEligibility | null>(null);
  const [testEligibilityDialogOpen, setTestEligibilityDialogOpen] = useState(false);
  const previousEligibilityStatusRef = useRef<string | null>(null);

  const payoutState = useMemo(
    () => computePayoutState(
      { validatedAudios, pendingAudios, totalContributions, testApproved, validatedAudiosAfterTest, pendingPayoutAmount, backendEligibility },
      tPage,
    ),
    [validatedAudios, pendingAudios, testApproved, validatedAudiosAfterTest, totalContributions, pendingPayoutAmount, backendEligibility],
  );

  useEffect(() => {
    const currentStatus = payoutState.eligibilityStatus;
    const previousStatus = previousEligibilityStatusRef.current;
    if (currentStatus === "PENDING_TEST" && previousStatus !== "PENDING_TEST" && payoutState.step1Reached && !testApproved) {
      setTestEligibilityDialogOpen(true);
    }
    previousEligibilityStatusRef.current = currentStatus;
  }, [payoutState.eligibilityStatus, payoutState.step1Reached, testApproved]);

  const loadUserStatistics = async () => {
    if (!user?.id) return;
    setLoadingUserStats(true);
    try {
      const [stats, situation, eligibility] = await Promise.all([
        contributionsApi.getUserStats(String(user.id)),
        paymentsApi.getContributorPayoutSituation(Number(user.id)).catch(() => null),
        testsApi.getContributorEligibility().catch(() => null),
      ]);
      const summary = summarizeContributorStats(stats, situation, eligibility);
      setTotalContributions(summary.totalContributions);
      setValidatedAudios(summary.validatedAudios);
      setPendingAudios(summary.pendingAudios);
      setAcceptedAudiosByLanguage(summary.acceptedByLanguage);
      setTestEligibility(eligibility);
      if (summary.backend) {
        setBackendEligibility(summary.backend.eligibility);
        setTestApproved(summary.backend.testApproved);
        setValidatedAudiosAfterTest(summary.backend.validatedAudiosAfterTest);
      }
    } catch {
      // The page keeps its last numbers.
    } finally {
      setLoadingUserStats(false);
    }
  };

  useEffect(() => {
    if (user?.id && canAccessContributorWorkspace(user.role)) {
      loadUserStatistics();
      loadUserContracts();
    }
  }, [user?.id, user?.role]);

  return {
    validatedAudios, pendingAudios, totalContributions,
    testApproved, validatedAudiosAfterTest,
    testEligibility, backendEligibility, testEligibilityDialogOpen,
    payoutState,
  };
}
